import TOKEN_NAMES from './token_names';

// staging's token_names.ts only default-exports the raw symbol->name map
// (no getTokenNameBySymbol helper, unlike development's version) - inlined
// equivalent so this file doesn't need a change to that shared module.
const getTokenNameBySymbol = (symbol: string): string | undefined =>
  TOKEN_NAMES[symbol] ?? TOKEN_NAMES[symbol.toUpperCase()];

const ICON_CDN_BASE_URL =
  'https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@v0.18.1/svg/color/';
const PUBLIC_ICON_BASE_URL = '/cripto_icons';
const DEFAULT_ICON_SRC = `${PUBLIC_ICON_BASE_URL}/GENERIC.svg`;

const currencyIcons = import.meta.glob('../assets/icons/currency/*.{svg,png}', {
  eager: true,
  import: 'default',
}) as Record<string, string>;
const publicIcons = import.meta.glob('/public/cripto_icons/*.svg');

const appendCandidate = (candidates: string[], nextCandidate?: string) => {
  if (nextCandidate && !candidates.includes(nextCandidate)) {
    candidates.push(nextCandidate);
  }
};

const normalizeAssetKey = (value: string) =>
  value
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

const getFileName = (filePath: string) => filePath.split('/').pop();

const buildFileNameLookup = (filePaths: string[]) =>
  filePaths.reduce<Record<string, string>>((lookup, filePath) => {
    const fileName = getFileName(filePath);

    if (!fileName) {
      return lookup;
    }

    lookup[normalizeAssetKey(fileName)] = fileName;

    return lookup;
  }, {});

const currencyIconLookup = Object.entries(currencyIcons).reduce<Record<string, string>>(
  (lookup, [filePath, iconUrl]) => {
    const fileName = getFileName(filePath);

    if (!fileName) {
      return lookup;
    }

    lookup[normalizeAssetKey(fileName)] = iconUrl;

    return lookup;
  },
  {},
);
const publicIconLookup = buildFileNameLookup(Object.keys(publicIcons));

const getSymbolLookupCandidates = (symbol: string) => {
  const trimmedSymbol = symbol.trim();

  if (!trimmedSymbol) {
    return [];
  }

  const tokenCode = trimmedSymbol.toUpperCase();
  const tokenName = getTokenNameBySymbol(trimmedSymbol);

  return Array.from(new Set([tokenCode, trimmedSymbol, tokenName].filter(Boolean) as string[]));
};

const appendLocalAndPublicCandidates = (symbol: string, candidates: string[]) => {
  const lookupCandidates = getSymbolLookupCandidates(symbol);

  for (const candidate of lookupCandidates) {
    appendCandidate(candidates, currencyIconLookup[normalizeAssetKey(candidate)]);

    const fileName = publicIconLookup[normalizeAssetKey(candidate)];

    if (fileName) {
      appendCandidate(candidates, `${PUBLIC_ICON_BASE_URL}/${fileName}`);
    }
  }
};

export const getDefaultTokenIconSrc = () => DEFAULT_ICON_SRC;

export const getTokenIconCandidates = (symbol: string) => {
  const trimmedSymbol = symbol.trim();

  if (!trimmedSymbol) {
    return [DEFAULT_ICON_SRC];
  }

  const tokenCode = trimmedSymbol.toUpperCase();
  const candidates: string[] = [];

  appendLocalAndPublicCandidates(trimmedSymbol, candidates);
  appendCandidate(candidates, `${ICON_CDN_BASE_URL}${tokenCode.toLowerCase()}.svg`);
  appendCandidate(candidates, DEFAULT_ICON_SRC);

  return candidates;
};

export const getPreferredTokenIconSrc = (symbol: string) =>
  getTokenIconCandidates(symbol)[0] ?? DEFAULT_ICON_SRC;
